import { isType } from '@mtna/core-ts';
import { RelationalOperator, RelationOperatorSearchTextType } from '@mtna/data-core-ts';

/** `RelationalOperator`s that are applicable to text filters */
export type RdsTextOperations = Extract<RelationalOperator, 'EQ' | 'NE' | 'MATCHES' | 'NOT_MATCHES' | 'LIKE' | 'NOT_LIKE'>;

/** Available text filters */
export type RdsTextParameter =
  | Extract<RelationalOperator, 'EQ' | 'NE' | 'MATCHES' | 'NOT_MATCHES'>
  | 'CONTAINS'
  | 'NOT_CONTAINS'
  | 'STARTS_WITH'
  | 'NOT_STARTS_WITH'
  | 'NOT_ENDS_WITH'
  | 'ENDS_WITH';

/** Record to lookup the `RelationOperatorSearchTextType` to use for a given `RdsTextParameter`. Used to serialize text filters to url. */
export const RdsTextParameterSearchText: Record<RdsTextParameter, RelationOperatorSearchTextType> = {
  EQ: '=',
  NE: '!=',
  MATCHES: ' matches ',
  NOT_MATCHES: ' !matches ',
  STARTS_WITH: ' like ',
  CONTAINS: ' like ',
  ENDS_WITH: ' like ',
  NOT_STARTS_WITH: ' !like ',
  NOT_CONTAINS: ' !like ',
  NOT_ENDS_WITH: ' !like ',
};

export function invertTextParameter(parameter: RdsTextParameter): RdsTextParameter {
  switch (parameter) {
    case 'EQ':
      return 'NE';
    case 'NE':
      return 'EQ';
    case 'CONTAINS':
      return 'NOT_CONTAINS';
    case 'NOT_CONTAINS':
      return 'CONTAINS';
    case 'STARTS_WITH':
      return 'NOT_STARTS_WITH';
    case 'NOT_STARTS_WITH':
      return 'STARTS_WITH';
    case 'ENDS_WITH':
      return 'NOT_ENDS_WITH';
    case 'NOT_ENDS_WITH':
      return 'ENDS_WITH';
    case 'MATCHES':
      return 'NOT_MATCHES';
    case 'NOT_MATCHES':
      return 'MATCHES';
    default:
      // Cannot invert, return original
      return parameter;
  }
}

export class RdsTextParameterOption {
  constructor(public label: string, public type: RdsTextParameter) {}
}

export const RDS_TEXT_PARAMETER_OPTIONS: ReadonlyArray<RdsTextParameterOption> = Object.freeze([
  new RdsTextParameterOption($localize`contains`, 'CONTAINS'),
  new RdsTextParameterOption($localize`matches`, 'MATCHES'),
  new RdsTextParameterOption($localize`starts with`, 'STARTS_WITH'),
  new RdsTextParameterOption($localize`ends with`, 'ENDS_WITH'),
  new RdsTextParameterOption($localize`equals`, 'EQ'),
  new RdsTextParameterOption($localize`does not contain`, 'NOT_CONTAINS'),
  new RdsTextParameterOption($localize`does not match`, 'NOT_MATCHES'),
  new RdsTextParameterOption($localize`does not start with`, 'NOT_STARTS_WITH'),
  new RdsTextParameterOption($localize`does not end with`, 'NOT_ENDS_WITH'),
  new RdsTextParameterOption($localize`does not equal`, 'NE'),
]);

export class RdsTextFilter {
  /**
   * Creates an instance of RdsTextFilter
   * @param text text input
   * @param parameter RdsTextParameterOption
   */
  constructor(public text: string | null, public parameter: RdsTextParameterOption | null) {}
}

export function instanceOfRdsTextFilter(something: unknown): something is RdsTextFilter {
  return isType<RdsTextFilter>(something, 'text', 'parameter');
}
